import { useEffect } from 'react';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';

/**
 * Production-only global error handlers.
 * Console suppression is handled in main.tsx (outside React lifecycle)
 * to prevent restore/re-suppress gaps during StrictMode remounts.
 */
export function ProductionOptimizations() {
  const { trackError } = useAnalyticsTracking();

  useEffect(() => {
    if (import.meta.env.DEV) return;

    // Global error handler for unhandled errors
    const handleGlobalError = (event: ErrorEvent) => {
      trackError(new Error(event.message), 'global_error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    };

    // Global promise rejection handler
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(`Unhandled Promise Rejection: ${event.reason}`);

      trackError(error, 'unhandled_promise_rejection', {
        reason: event.reason,
        stack: error.stack,
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // NOTE: CLS and Long Task monitoring is consolidated in usePerformanceMonitor.
    // Do NOT add duplicate PerformanceObservers here.

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [trackError]);

  return null;
}
